import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BadgeList } from '../display/BadgeList'
import { ConfigRow } from '../display/ConfigRow'
import { ConfigSection } from '../display/ConfigSection'
import { EditableConfigRow } from '../display/EditableConfigRow'
import { RegionsAccordion } from '../display/RegionsAccordion'
import type { ConfigQueryResult } from '../PartnerConfig.types'

import { MultiSelect, type SelectOption } from '@percent/lemonade'
import type { GivingRegionsConfigValue } from '@percent/admin-dashboard/api/types'
import { useLocalisationSection } from './localisation/useLocalisationSection'

type LocalisationSectionProps = {
  currencies: ConfigQueryResult<string[]>
  regions: ConfigQueryResult<GivingRegionsConfigValue>
  calendarIntegration: ConfigQueryResult<string[]>
  preferredCountries: ConfigQueryResult<string[]>
  isSaving: boolean
  savingKey: string | null
  mutationError?: string
  resetSaving: () => void
  saveCalendarIntegration: (value: string[], onSuccess?: () => void) => void
}

export function LocalisationSection({
  currencies,
  regions,
  calendarIntegration,
  preferredCountries,
  isSaving,
  savingKey,
  mutationError,
  resetSaving,
  saveCalendarIntegration
}: LocalisationSectionProps) {
  const { t } = useTranslation()

  const calendarOptions = useMemo<SelectOption[]>(
    () => [
      { value: 'google', label: t('partnerConfig.localisation.calendar.google') },
      { value: 'outlook', label: t('partnerConfig.localisation.calendar.outlook') }
    ],
    [t]
  )

  const { editingKey, startEditing, cancel, errorForKey, calendarDraft, setCalendarDraft } = useLocalisationSection({
    calendarData: calendarIntegration.data,
    savingKey,
    isSaving,
    mutationError,
    resetSaving
  })

  // Stable ref so useMultiSelect's defaultValue effect only re-seeds when the draft actually
  // changes; an inline .map() would be a new array every render, re-triggering that effect.
  const calendarSelected = useMemo<SelectOption[]>(
    () => calendarDraft.map(val => calendarOptions.find(opt => opt.value === val) ?? { value: val, label: val }),
    [calendarDraft, calendarOptions]
  )

  return (
    <ConfigSection title={t('partnerConfig.section.localisation')}>
      <ConfigRow
        id="localisation.currencies"
        label={t('partnerConfig.localisation.currencies')}
        isLoading={currencies.isLoading}
        error={currencies.error}
      >
        {currencies.data?.length ? <BadgeList items={currencies.data} /> : undefined}
      </ConfigRow>

      <EditableConfigRow
        id="localisation.calendarIntegration"
        label={t('partnerConfig.localisation.calendarIntegration')}
        isLoading={calendarIntegration.isLoading}
        error={calendarIntegration.error}
        isEditing={editingKey === 'calendarIntegration'}
        isSaving={savingKey === 'calendar_integration' && isSaving}
        saveError={errorForKey('calendar_integration')}
        onEdit={() => startEditing('calendarIntegration')}
        onCancel={cancel}
        saveDisabled={isSaving}
        onSave={() => saveCalendarIntegration(calendarDraft, cancel)}
        displayContent={calendarIntegration.data?.length ? <BadgeList items={calendarIntegration.data} /> : undefined}
        editContent={
          <MultiSelect
            options={calendarOptions}
            defaultValue={calendarSelected}
            onChange={opts => setCalendarDraft(opts.map(opt => opt.value))}
            placeholder={t('partnerConfig.localisation.edit.calendarPlaceholder')}
            allowClear
          />
        }
      />

      <ConfigRow
        id="localisation.regions"
        label={t('partnerConfig.localisation.regions')}
        isLoading={regions.isLoading}
        error={regions.error}
      >
        {regions.data && Object.keys(regions.data).length > 0 ? <RegionsAccordion regions={regions.data} /> : undefined}
      </ConfigRow>

      <ConfigRow
        id="localisation.preferredCountries"
        label={t('partnerConfig.localisation.preferredCountries')}
        isLoading={preferredCountries.isLoading}
        error={preferredCountries.error}
      >
        {preferredCountries.data?.length ? <BadgeList items={preferredCountries.data} /> : undefined}
      </ConfigRow>
    </ConfigSection>
  )
}
